import React from 'react';
import ReviewIcon from 'assets/images/riviewdicon.svg';
import { formatCurrency } from 'utils';
import type { AuditSummaryContext, ManualOverallCollectionsTileConfig, TileBadge, TileConfig } from '../types';
import { resolveVarianceBadge } from './shared';

const resolveBalanceColor = (badge: TileBadge) => {
  if (badge === 'MATCHED') return '#34C759';
  return badge === 'SURPLUS' ? '#0000FF' : '#FF0000';
};

export function buildOverallTile(ctx: AuditSummaryContext): TileConfig {
  if (!ctx.isAllShift) {
    return {
      id: 'overall',
      visible: false,
      variant: 'standard_summary',
      title: 'OVERALL',
    };
  }

  if (ctx.manualDayEntryEditability.collections) {
    const useBreakdownOverall = ctx.mode === 'bothNotConnected' || ctx.mode === 'lottoOnly';

    const hasManualLottoEntry =
      ctx.manualDayEntryEditability.lotto &&
      (ctx.manualFormValues.lottoSalesAmount !== '' || ctx.manualFormValues.lottoPayoutsAmount !== '');

    const effectiveLottoSales = hasManualLottoEntry
      ? Number(ctx.manualFormValues.lottoSalesAmount) || 0
      : (ctx.dailyTotals?.lotto?.sales ?? 0);
    const effectivePayouts = hasManualLottoEntry
      ? Number(ctx.manualFormValues.lottoPayoutsAmount) || 0
      : (ctx.dailyTotals?.lotto?.payouts_total ?? 0);

    const effectiveScratcherSales = ctx.dailyTotals?.audit?.total_revenue ?? 0;

    const hasManualCollectionsEntry =
      ctx.manualFormValues.cashCollectionAmount !== '' || ctx.manualFormValues.creditCardCollectionAmount !== '';

    const effectiveCashCollection = hasManualCollectionsEntry
      ? Number(ctx.manualFormValues.cashCollectionAmount) || 0
      : (ctx.dailyTotals?.collections?.cash_collection ?? 0);
    const effectiveCreditCardCollection = hasManualCollectionsEntry
      ? Number(ctx.manualFormValues.creditCardCollectionAmount) || 0
      : (ctx.dailyTotals?.collections?.credit_card_collection ?? 0);

    const totalCollections = effectiveCashCollection + effectiveCreditCardCollection;
    const totalVariance = totalCollections + effectivePayouts - effectiveScratcherSales - effectiveLottoSales;
    const badge = resolveVarianceBadge(totalVariance);
    const isMatched = totalVariance === 0;

    const config: ManualOverallCollectionsTileConfig = {
      id: 'overall',
      visible: true,
      variant: 'manual_overall_collections',
      title: 'OVERALL',
      useBreakdownOverall,
      effectivePayouts,
      effectiveScratcherSales,
      effectiveLottoSales,
      totalVariance,
      isMatched,
      balanceLabel: badge,
      balanceBadgeClassName:
        badge === 'MATCHED' ? 'matched-badge' : badge === 'SURPLUS' ? 'surplus-badge' : 'deficit-badge',
      balanceColor: resolveBalanceColor(badge),
      cardClassName: useBreakdownOverall
        ? 'compact-overall-manual-card'
        : 'manual-entry-card overall-manual-entry-card',
      footer: useBreakdownOverall ? undefined : 'Manual Collection Entry',
    };

    return config;
  }

  if (ctx.mode !== 'bothConnected' && ctx.mode !== 'posOnly') {
    return {
      id: 'overall',
      visible: false,
      variant: 'standard_summary',
      title: 'OVERALL',
    };
  }

  const baseTotalVariance = ctx.dailyTotals?.reconciliation_variance?.total_variance ?? 0;
  const baseScratcherRevenueVariance = ctx.dailyTotals?.reconciliation_variance?.scratcher?.revenue_variance ?? 0;
  const baseLottoSalesVariance = ctx.dailyTotals?.reconciliation_variance?.lotto_sales_variance ?? 0;
  const basePayoutVariance = ctx.dailyTotals?.reconciliation_variance?.payout_variance ?? 0;
  const scratcherAuditRevenue = ctx.dailyTotals?.audit?.total_revenue ?? 0;
  const scratcherPosRevenue = ctx.dailyTotals?.pos?.scratcher_sales ?? 0;
  const hasScratcherSalesInput = ctx.isPosValueMissing && ctx.posMissingScratcherSalesInput !== '';
  const effectiveScratcherPosRevenue = hasScratcherSalesInput
    ? Number(ctx.posMissingScratcherSalesInput) || 0
    : scratcherPosRevenue;
  const effectiveScratcherRevenueVariance = effectiveScratcherPosRevenue - scratcherAuditRevenue;
  const lottoSalesInLotto = ctx.dailyTotals?.lotto?.sales ?? 0;
  const lottoSalesInPOS = ctx.dailyTotals?.pos?.lotto_sales ?? 0;
  const hasLottoSalesInput = ctx.isPosValueMissing && ctx.posMissingLottoSalesInput !== '';
  const effectivePosLottoSales = hasLottoSalesInput
    ? Number(ctx.posMissingLottoSalesInput) || 0
    : lottoSalesInPOS;
  const effectiveLottoSalesVariance = effectivePosLottoSales - lottoSalesInLotto;
  const payoutsInLotto = ctx.dailyTotals?.lotto?.payouts_total ?? 0;
  const freeTicketsPaid = ctx.dailyTotals?.lotto?.scratcher_free_tickets_paid_amount ?? 0;
  const payoutsInPOS = ctx.dailyTotals?.pos?.payout ?? 0;
  const hasPayoutInput = ctx.isPosValueMissing && ctx.posMissingPayoutInput !== '';
  const effectivePosPayout = hasPayoutInput
    ? Number(ctx.posMissingPayoutInput) || 0
    : payoutsInPOS;
  const effectivePayoutVariance = (payoutsInLotto + freeTicketsPaid) - effectivePosPayout;
  const totalVariance =
    baseTotalVariance
    - baseScratcherRevenueVariance
    + effectiveScratcherRevenueVariance
    - baseLottoSalesVariance
    + effectiveLottoSalesVariance
    - basePayoutVariance
    + effectivePayoutVariance;
  const badge = resolveVarianceBadge(totalVariance);
  const hasNoVariance = totalVariance === 0;

  return {
    id: 'overall',
    visible: true,
    variant: 'overall_lotto_pos',
    title: 'OVERALL',
    badge,
    value: hasNoVariance ? (
      <img
        src={ReviewIcon}
        alt="Review"
        className="review-icon"
        style={{ width: '32px', height: '32px' }}
      />
    ) : (
      formatCurrency(totalVariance)
    ),
    footer: '',
    actionLabel: 'Acknowledged',
    cardClassName:
      ctx.mode === 'posOnly'
        ? 'manual-entry-card manual-entry-centered-card'
        : ctx.mode === 'bothConnected' && ctx.isPosValueMissing
          ? 'pos-missing-input-card overall-pos-missing-card'
        : undefined,
  };
}
